if (!customElements.get('wlsn-buybox-sticky')) {
  class WLSNStickyBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        buybox: 'wlsn-buybox',
        footer: 'wlsn-footer',
        frequencyForm: 'wlsn-product-form-frequency',
        stickySelector: 'wlsn-options-selector',
        subscribeButton: 'js-buybox-sticky-subscribe-button',
        inputPurchaseTypeSubscription: 'input[name="purchase-type"][value="subscription"]',
        inputPurchaseTypeChecked: 'input[name="purchase-type"]:checked',
      };

      // Array of selectors that should hide the sticky buybox when visible
      this.hideConditions = [
        {
          selector: `.${this.classes.buybox}`,
          description: 'Buybox',
        },
        {
          selector: `.${this.classes.footer}`,
          description: 'Footer',
        },
      ];

      this.observers = {};
      this.visibilityStates = {};
      this.elementsCache = null;

      // Bind methods
      this.boundHandleResize = this._handleResize.bind(this);
      this.boundHandleButtonClick = this._handleButtonClick.bind(this);
      this.boundHandlePurchaseTypeChange = this._handlePurchaseTypeChange.bind(this);

      // Flag to prevent infinite sync loops
      this.isSyncing = false;

      this.purchaseType = {
        OTP: 'one-time',
        SUB: 'subscription',
      };
    }

    /**
     * Get cached elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      if (!this.elementsCache) {
        this.elementsCache = {
          buybox: this.closest(`${this.classes.buybox}`),
          stickySelector: this.querySelector(`${this.classes.stickySelector}`),
          subscribeButton: this.querySelector(`.${this.classes.subscribeButton}`),
          frequencyForm: document.querySelector(`${this.classes.frequencyForm}`),
        };
      }
      return this.elementsCache;
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      // Wait for DOM to be ready
      setTimeout(() => {
        this._init();
      }, 0);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._cleanup();
    }

    /**
     * Initialize the sticky buybox
     * @returns {void}
     */
    _init() {
      if (!this.elements.buybox) {
        console.warn('[WLSNStickyBuybox] Missing required elements');
        return;
      }

      this._setupHideConditions();
      this._setupIntersectionObservers();
      this._bindEvents();
      this._initializePurchaseTypeDisplay();
    }

    /**
     * Setup hide conditions and cache elements
     * @returns {void}
     */
    _setupHideConditions() {
      // Initialize visibility states for all hide conditions
      this.hideConditions.forEach((condition, index) => {
        const element = document.querySelector(condition.selector);
        if (element) {
          this.elementsCache[`condition${index}`] = element;
          this.visibilityStates[`condition${index}`] = false;
        } else {
          console.warn(
            `[WLSNStickyBuybox] Could not find element for ${condition.description} (${condition.selector})`
          );
        }
      });
    }

    /**
     * Setup intersection observers for hide conditions
     * @returns {void}
     */
    _setupIntersectionObservers() {
      // Create observers for each hide condition
      this.hideConditions.forEach((condition, index) => {
        const element = this.elements[`condition${index}`];
        if (!element) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Consider visible if more than 20% is visible
              const isVisible = entry.intersectionRatio > 0.2;

              this.visibilityStates[`condition${index}`] = isVisible;
              this._updateStickyState();
            });
          },
          {
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
            rootMargin: '0px 0px 0px 0px',
          }
        );

        observer.observe(element);
        this.observers[`condition${index}`] = observer;
      });
    }

    /**
     * Bind event listeners
     * @returns {void}
     */
    _bindEvents() {
      window.addEventListener('resize', this.boundHandleResize, { passive: true });

      // Listen for clicks on subscribe button (to activate subscription)
      if (this.elements.subscribeButton) {
        this.elements.subscribeButton.addEventListener('click', this.boundHandleButtonClick);
      }

      // Listen for purchase type changes from main form
      window.addEventListener('wlsn:purchase-type:change', this.boundHandlePurchaseTypeChange);
    }

    /**
     * Unbind event listeners
     * @returns {void}
     */
    _unbindEvents() {
      window.removeEventListener('resize', this.boundHandleResize);

      if (this.elements.subscribeButton) {
        this.elements.subscribeButton.removeEventListener('click', this.boundHandleButtonClick);
      }

      window.removeEventListener('wlsn:purchase-type:change', this.boundHandlePurchaseTypeChange);
    }

    /**
     * Handle window resize
     * @returns {void}
     */
    _handleResize() {
      // Force update sticky state on resize
      this._updateStickyState();
    }

    /**
     * Handle subscribe button click event
     * Activates subscription mode and selects first selling plan
     * @param {Event} event - The click event
     * @returns {void}
     */
    _handleButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const subscriptionRadio = this.elements.frequencyForm?.querySelector(this.classes.inputPurchaseTypeSubscription);

      if (subscriptionRadio && !subscriptionRadio.checked) {
        subscriptionRadio.checked = true;
        subscriptionRadio.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      }
    }

    /**
     * Handle purchase type change event from main form
     * Shows/hides button vs selector based on purchase type
     * @param {CustomEvent} event - The custom event
     * @returns {void}
     */
    _handlePurchaseTypeChange(event) {
      const { purchaseType } = event.detail;
      this._purchaseTypeState(purchaseType);
    }

    /**
     * Initialize purchase type display based on main form
     * @returns {void}
     */
    _initializePurchaseTypeDisplay() {
      const checkedPurchaseType = this.elements.frequencyForm?.querySelector(this.classes.inputPurchaseTypeChecked);

      if (!checkedPurchaseType) return;

      const purchaseType = checkedPurchaseType.value;

      this._purchaseTypeState(purchaseType);
    }

    /**
     * Get all data attributes from an item
     * @param {HTMLElement} item - The item element
     * @returns {object} - Object containing all data attributes
     */
    _getItemDataAttributes(item) {
      const dataAttributes = {};
      Array.from(item.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) {
          const key = attr.name.slice(5).replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          dataAttributes[key] = attr.value;
        }
      });
      return dataAttributes;
    }

    /**
     * Handle purchase type state
     * Shows/hides button vs selector based on purchase type
     * @param {string} purchaseType - The purchase type value
     * @returns {void}
     */
    _purchaseTypeState(purchaseType) {
      const { subscribeButton, stickySelector } = this.elements;

      if (!subscribeButton || !stickySelector) return;

      if (purchaseType === this.purchaseType.SUB) {
        subscribeButton.classList.add(this.classes.hidden);
        stickySelector.classList.remove(this.classes.hidden);
      } else {
        subscribeButton.classList.remove(this.classes.hidden);
        stickySelector.classList.add(this.classes.hidden);
      }
    }

    /**
     * Update sticky state based on visibility conditions
     * @returns {void}
     */
    _updateStickyState() {
      // Check if any hide condition is visible
      const anyHideConditionVisible = Object.values(this.visibilityStates).some((state) => state === true);

      // Show sticky buybox when NO hide conditions are visible
      const shouldShowSticky = !anyHideConditionVisible;

      if (shouldShowSticky) {
        this._showSticky();
      } else {
        this._hideSticky();
      }
    }

    /**
     * Show sticky buybox
     * @returns {void}
     */
    _showSticky() {
      if (!this.classList.contains(this.classes.active)) {
        this.classList.add(this.classes.active);
      }
    }

    /**
     * Hide sticky buybox
     * @returns {void}
     */
    _hideSticky() {
      if (!this.classList.contains(this.classes.active)) {
        return;
      }
      this.classList.remove(this.classes.active);
    }

    /**
     * Cleanup on disconnect
     * @returns {void}
     */
    _cleanup() {
      // Disconnect all observers
      Object.values(this.observers).forEach((observer) => {
        if (observer) {
          observer.disconnect();
        }
      });

      // Unbind events
      this._unbindEvents();

      // Clear cache
      this.elementsCache = null;
    }
  }

  customElements.define('wlsn-buybox-sticky', WLSNStickyBuybox);
}
